import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { isApiError } from '@/lib/api/errors';
import { useAuth, type Company } from '@/lib/auth/auth';
import { Button } from '@/ui/button';
import { Field, Input, Select } from '@/ui/form';

const schema = z.object({ email: z.string().min(1).email(), password: z.string().min(1) });
type Values = z.infer<typeof schema>;

/** Only same-origin relative paths are accepted as a return URL (no open redirect). */
const safeRedirect = (r?: string) => (r && r.startsWith('/') && !r.startsWith('//') ? r : '/');

export function LoginPage({ redirect }: { redirect?: string }) {
  const { t } = useTranslation();
  const { login, verifyMfa, sessionExpired } = useAuth();
  const [mfa, setMfa] = useState(false);
  const [code, setCode] = useState('');
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<Values>({ resolver: zodResolver(schema) });

  async function submit(v: Values, chosen?: string) {
    setError(null);
    try {
      if ((await login(v.email, v.password, chosen)) === 'mfa') { setMfa(true); return; }
      await navigate({ to: safeRedirect(redirect), replace: true } as never);
    } catch (e) {
      if (isApiError(e) && e.code === 'CONFLICT') {
        const list = (e.details as { companies?: Company[] } | undefined)?.companies;
        if (list?.length) { setCompanies(list); setCompanyId(list[0]!.id); return; }
      }
      setError(isApiError(e) ? (e.status === 401 ? t('auth.invalid') : t(`errors.${e.code}`)) : t('errors.INTERNAL_ERROR'));
    }
  }

  async function submitCode() {
    setError(null);
    try { await verifyMfa(code.trim()); await navigate({ to: safeRedirect(redirect), replace: true } as never); }
    catch (e) { setError(isApiError(e) ? (e.status === 401 ? t('sec.badCode') : t(`errors.${e.code}`)) : t('errors.INTERNAL_ERROR')); setCode(''); }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative hidden overflow-hidden bg-[#0b1220] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -end-32 -top-32 size-[520px] rounded-full bg-[#2f5bea]/30 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-40 start-0 size-[420px] rounded-full bg-[#12805c]/20 blur-3xl" aria-hidden />
        <div className="relative inline-flex w-fit rounded-2xl bg-white px-5 py-3 shadow-lg"><img src="/logo-full.png" alt={t('app.name')} className="h-14 w-auto" /></div>
        <div className="relative max-w-md space-y-4">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">Facturation, trésorerie et relation client, au même endroit.</h2>
          <p className="text-[15px] leading-relaxed text-white/65">Conforme TTN, multi-sociétés, pensé pour la précision comptable.</p>
        </div>
        <p className="relative flex items-center gap-2 text-sm text-white/55"><ShieldCheck className="size-4" aria-hidden />TEIF · XAdES · Tunisie</p>
      </aside>

      <main className="flex items-center justify-center px-5 py-10">
        {mfa ? (
          <form onSubmit={(e) => { e.preventDefault(); void submitCode(); }} className="animate-in w-full max-w-sm space-y-6">
            <div className="space-y-1.5"><h1 className="text-2xl font-semibold tracking-tight">{t('sec.mfaLoginTitle')}</h1><p className="text-sm text-muted">{t('sec.mfaLoginHint')}</p></div>
            {error && <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">{error}</p>}
            <Field label={t('sec.codeOrRecovery')} required>{(id) => <Input id={id} autoFocus inputMode="text" autoComplete="one-time-code" className="tnum text-center text-lg tracking-widest" value={code} onChange={(e) => setCode(e.target.value)} />}</Field>
            <Button type="submit" variant="primary" size="lg" className="w-full" disabled={code.trim().length < 6}>{t('sec.verify')}</Button>
            <button type="button" onClick={() => { setMfa(false); setCode(''); setError(null); }} className="w-full text-center text-sm text-muted underline-offset-2 hover:underline">{t('sec.backToLogin')}</button>
          </form>
        ) : (
        <form onSubmit={handleSubmit((v) => submit(v, companies ? companyId : undefined))} className="animate-in w-full max-w-sm space-y-6" noValidate>
          <div className="space-y-1.5">
            <div className="mb-4 w-fit rounded-xl bg-white px-3 py-2 lg:hidden"><img src="/logo-full.png" alt={t('app.name')} className="h-10 w-auto" /></div>
            <h1 className="text-2xl font-semibold tracking-tight">{companies ? t('auth.chooseCompany') : t('auth.title')}</h1>
            <p className="text-sm text-muted">{companies ? t('auth.chooseCompanyHint') : t('auth.subtitle')}</p>
          </div>

          {sessionExpired && !error && <p role="status" className="rounded-md border border-warning-border bg-warning-soft px-3 py-2 text-sm text-warning">{t('auth.sessionEnded')}</p>}
          {error && <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">{error}</p>}

          {!companies ? (
            <div className="grid gap-4">
              <Field label={t('auth.email')} error={errors.email && t('errors.VALIDATION_ERROR')} required>
                {(id, d) => <Input id={id} type="email" autoComplete="username" autoFocus aria-invalid={!!errors.email} aria-describedby={d} {...register('email')} />}
              </Field>
              <Field label={t('auth.password')} error={errors.password && t('errors.VALIDATION_ERROR')} required>
                {(id, d) => <Input id={id} type="password" autoComplete="current-password" aria-invalid={!!errors.password} aria-describedby={d} {...register('password')} />}
              </Field>
            </div>
          ) : (
            <Field label={t('auth.company')}>
              {(id) => <Select id={id} value={companyId} onChange={(e) => setCompanyId(e.target.value)}>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>}
            </Field>
          )}

          <Button type="submit" variant="primary" size="lg" className="w-full" loading={isSubmitting}>
            {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
          {!companies && <Link to="/forgot-password" className="block text-center text-sm text-muted underline-offset-2 hover:underline">{t('reset.forgotLink')}</Link>}
        </form>
        )}
      </main>
    </div>
  );
}
